/**
 * views/DetectionLab.tsx — embedded in ManagerConsole (admin / super_admin only)
 * ──────────────────────────────────────────────────────────────────────────────
 * Phase 2 of the Detection Lab: runtime pipeline tuning + test runs.
 *
 *  ① Toggle panel  — Preprocessing | Decoders | OCR, driven by the server's
 *    stage-availability map (unavailable stages render disabled with a reason).
 *  ② Save active config — PUT /api/detection/config (runtime-switchable).
 *  ③ Run on test data — upload images, chunked POSTs to /api/detection/run
 *    with the *current* UI toggles (saved or not), merged client-side.
 *  ④ Results — per-file decode ✓/✗, value, winning decoder, per-stage ms,
 *    annotated preview (detection boxes), and p50/p95 across the run.
 *  ⑤ Comparison table — accumulates runs in session state only (by design;
 *    nothing persisted server-side). Presets land in Phase 3.
 *  ⑥ SOH ground truth (optional) — upload a WMS Stock-on-Hand export; decoded
 *    QR/OCR values are checked against its Location / SSCC / Lot sets and
 *    flagged SOH-verified. Session-only, computed client-side.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, ApiError } from '@/api/client';

// ── API types (local — single consumer) ───────────────────────────────────────

interface StageInfo {
  name: string;
  label: string;
  available: boolean;
  reason: string | null;
}

interface StagesResponse {
  preprocessors: StageInfo[];
  decoders: StageInfo[];
  ocr: StageInfo[];
}

interface PipelineConfig {
  preprocessors: Record<string, boolean>;
  decoders: Record<string, boolean>;
  ocr: { tesseract: boolean; mode: 'fallback' | 'parallel' };
}

interface ActiveConfigResponse {
  id: string;
  name: string;
  config: PipelineConfig;
  updated_at: string;
}

interface StageTimingOut {
  name: string;
  kind: string;
  ms: number;
  available: boolean;
  detections: number;
}

interface FileResultOut {
  filename: string;
  success: boolean;
  selected_value: string | null;   // location QR (slot identity)
  selected_decoder: string | null;
  sku_value?: string | null;       // top-ranked SKU (stock identity)
  sku_decoder?: string | null;
  // ALL distinct SKU/pallet labels, ranked — any one can identify the
  // inventory against SOH (pallet QR, SSCC, ITF-14 case codes…).
  skus?: { value: string; decoder?: string | null; symbology?: string | null }[];
  selection_ambiguous?: boolean;
  selection_audit_reason?: string | null;
  detections: { value: string; symbology: string; confidence: number; decoders: string[] }[];
  ocr: { text: string; confidence: number }[];
  timings: StageTimingOut[];
  total_ms: number;
  image_width: number;
  image_height: number;
  preview_b64: string | null;
  error?: string | null;
}

interface RunResponse {
  config: PipelineConfig;
  files: FileResultOut[];
  summary: Record<string, unknown>;
}

interface SessionRun {
  id: number;
  label: string;
  config: PipelineConfig;
  files: FileResultOut[];
  decodeRate: number;
  avgMs: number;
  p50: number;
  p95: number;
  ambiguousCount: number;
}

interface PresetResponse {
  id: string;
  name: string;
  is_active: boolean;
  config: PipelineConfig;
  created_at: string;
}

interface SOHGroundTruth {
  filename: string;
  rows: number;
  locations: string[];
  sscc: string[];
  pallet_codes: string[];
  lots: string[];
}

interface SavedRunSummary {
  id: string;
  label: string;
  created_at: string;
  file_count: number;
  decode_rate: number;
  avg_ms: number;
  p50_ms: number;
  p95_ms: number;
  ambiguous_count: number;
  sku_count: number;
  config: PipelineConfig;
}

interface SavedRunDetail extends SavedRunSummary {
  files: (Partial<FileResultOut> & { filename: string })[];
}

interface SOHSets {
  locations: Set<string>;
  sscc: Set<string>;
  palletCodes: Set<string>;
  lots: Set<string>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHUNK_SIZE = 6;          // files per POST — keeps each request fast
const MAX_TOTAL_FILES = 60;    // client-side cap per run

const DECODER_COLORS: Record<string, string> = {
  scandit: '#2563eb', pyzbar: '#16a34a', opencv_aruco: '#ea580c', zxingcpp: '#9333ea',
};

// White-label display names (CLAUDE.md): the operator must never SEE the
// third-party decoder-engine vendor. The internal ids stay as-is everywhere they
// are object keys / config flags / colour-lookup keys — only rendered TEXT is
// mapped through here, so any chip, legend, summary or run label shows a neutral
// engine name. Unknown ids fall through unchanged.
const DECODER_LABELS: Record<string, string> = {
  scandit: 'Engine A', zxingcpp: 'Engine B', pyzbar: 'Engine C', opencv_aruco: 'Engine D',
};
const decoderLabel = (id: string | null | undefined): string =>
  (id && DECODER_LABELS[id]) || id || '';

function percentile(values: number[], pct: number): number {
  if (values.length === 0) return 0;
  const ordered = [...values].sort((a, b) => a - b);
  const k = Math.max(0, Math.min(ordered.length - 1, Math.round((pct / 100) * (ordered.length - 1))));
  return ordered[k];
}

function configSummary(cfg: PipelineConfig): string {
  const dec = Object.entries(cfg.decoders).filter(([, on]) => on).map(([n]) => decoderLabel(n));
  const pre = Object.entries(cfg.preprocessors).filter(([, on]) => on).map(([n]) => n);
  const ocr = cfg.ocr.tesseract ? `ocr:${cfg.ocr.mode}` : 'ocr:off';
  return [pre.length ? `pre:${pre.join('+')}` : null, dec.join('+') || 'no-decoders', ocr]
    .filter(Boolean).join(' · ');
}

/** Non-empty subsets of the currently-enabled decoders (max 15 for 4 decoders). */
function decoderCombos(cfg: PipelineConfig): Array<{ label: string; config: PipelineConfig }> {
  const enabled = Object.entries(cfg.decoders).filter(([, on]) => on).map(([n]) => n);
  if (enabled.length === 0) return [];
  const combos: Array<{ label: string; config: PipelineConfig }> = [];
  for (let mask = 1; mask < (1 << enabled.length); mask++) {
    const subset = enabled.filter((_, i) => (mask >> i) & 1);
    const decoders: Record<string, boolean> = {};
    for (const name of Object.keys(cfg.decoders)) decoders[name] = subset.includes(name);
    // Config keys stay raw ids; only the display label is white-labelled.
    combos.push({ label: subset.map(decoderLabel).join('+'), config: { ...cfg, decoders } });
  }
  return combos;
}

/** Match one decoded value against the SOH ground truth.
 *  Tries exact (location / SSCC / lot), then digit-only forms so a barcode
 *  decoded as "(00)1941…" or "001941…" still matches its 18-digit SSCC. */
function matchSoh(value: string, sets: SOHSets): 'location' | 'sscc' | 'pallet' | 'lot' | null {
  const v = value.trim().toUpperCase();
  if (sets.locations.has(v)) return 'location';
  if (sets.sscc.has(v)) return 'sscc';
  if (sets.palletCodes.has(v)) return 'pallet';
  if (sets.lots.has(v)) return 'lot';
  const digits = v.replace(/\D/g, '');
  if (digits.length >= 18 && sets.sscc.has(digits.slice(-18))) return 'sscc';
  if (digits.length >= 6 && sets.lots.has(digits)) return 'lot';
  if (digits.length >= 4 && sets.palletCodes.has(digits)) return 'pallet';
  return null;
}

/** SOH verification status for one file result.
 *  'qr'  — a decoded barcode/QR value exists in the SOH file (strongest)
 *  'ocr' — no QR match, but OCR text contains an SOH lot/SSCC/location
 *  'miss' — decoded, but the value is not in the SOH file
 *  null  — nothing decoded and no OCR match (plain ✗ already covers it) */
function fileSohStatus(f: FileResultOut, sets: SOHSets): 'qr' | 'ocr' | 'miss' | null {
  const qrVals = [f.selected_value, ...f.detections.map(d => d.value)]
    .filter((v): v is string => !!v);
  if (qrVals.some(v => matchSoh(v, sets) !== null)) return 'qr';
  const text = f.ocr.map(o => o.text).join(' ').toUpperCase();
  if (text) {
    for (const run of text.match(/\d{6,}/g) ?? []) {
      if (sets.lots.has(run)) return 'ocr';
      if (run.length >= 18 && (sets.sscc.has(run) || sets.sscc.has(run.slice(-18)))) return 'ocr';
    }
    // Token membership covers every site's location/pallet format
    // (50-005-D, SHA-016-70, CA-001-B, D-020-B, 1500425-AGY725, …)
    for (const raw of text.split(/\s+/)) {
      const tok = raw.replace(/^[^A-Z0-9]+|[^A-Z0-9]+$/g, '');
      if (!tok) continue;
      if (sets.locations.has(tok) || sets.palletCodes.has(tok) || sets.lots.has(tok)) return 'ocr';
    }
  }
  return f.success ? 'miss' : null;
}

const SOH_BADGE: Record<'qr' | 'ocr' | 'miss', { label: string; cls: string }> = {
  qr:   { label: 'SOH ✓',     cls: 'bg-emerald-100 text-emerald-800' },
  ocr:  { label: 'OCR·SOH ✓', cls: 'bg-teal-100 text-teal-800' },
  miss: { label: 'not in SOH', cls: 'bg-amber-100 text-amber-800' },
};

function sortedRuns(
  runs: SessionRun[],
  key: 'label' | 'decodeRate' | 'avgMs' | 'p50' | 'p95',
  desc: boolean,
): SessionRun[] {
  const sorted = [...runs].sort((a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case 'label': av = a.label; bv = b.label; break;
      case 'decodeRate': av = a.decodeRate; bv = b.decodeRate; break;
      case 'avgMs': av = a.avgMs; bv = b.avgMs; break;
      case 'p50': av = a.p50; bv = b.p50; break;
      case 'p95': av = a.p95; bv = b.p95; break;
    }
    const cmp = typeof av === 'string' ? av.localeCompare(String(bv)) : av - (bv as number);
    return desc ? -cmp : cmp;
  });
  return sorted;
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function StageToggle({
  stage, checked, onChange,
}: { stage: StageInfo; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <label
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm
        ${stage.available ? 'cursor-pointer bg-white hover:bg-gray-50' : 'opacity-50 bg-gray-50 cursor-not-allowed'}`}
      title={stage.available ? stage.label : `Unavailable: ${stage.reason ?? 'library not installed'}`}
    >
      <input
        type="checkbox"
        checked={checked && stage.available}
        disabled={!stage.available}
        onChange={e => onChange(e.target.checked)}
      />
      <span
        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
        style={{ background: DECODER_COLORS[stage.name] ?? '#9ca3af' }}
      />
      <span className="font-medium">{stage.label}</span>
      {!stage.available && <span className="text-xs text-muted-foreground ml-auto">not installed</span>}
    </label>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────────

function PreviewModal({ file, onClose }: { file: FileResultOut; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={onClose}>
      {file.preview_b64 ? (
        <img
          src={file.preview_b64}
          alt={file.filename}
          className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <p className="text-white/60 text-sm">No preview available</p>
      )}
      <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono">
        {file.filename} · {file.image_width}×{file.image_height} · Esc to close
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DetectionLab() {
  const [stages, setStages]   = useState<StagesResponse | null>(null);
  const [config, setConfig]   = useState<PipelineConfig | null>(null);
  const [savedJson, setSavedJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving]   = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const [files, setFiles]     = useState<File[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [runError, setRunError] = useState<string | null>(null);

  const [runs, setRuns]       = useState<SessionRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [zoomFile, setZoomFile] = useState<FileResultOut | null>(null);
  const runCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Presets
  const [presets, setPresets]         = useState<PresetResponse[]>([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetMsg, setPresetMsg]     = useState<string | null>(null);

  // Batch run
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ combo: 0, totalCombos: 0, image: 0, totalImages: 0 });

  // Comparison table sort
  type SortKey = 'label' | 'decodeRate' | 'avgMs' | 'p50' | 'p95';
  const [sortKey, setSortKey] = useState<SortKey>('decodeRate');
  const [sortDesc, setSortDesc] = useState(true);  // desc for decode rate (higher better), asc for latency

  // SOH ground truth (optional, session-only)
  const [soh, setSoh] = useState<SOHGroundTruth | null>(null);
  const [sohLoading, setSohLoading] = useState(false);
  const [sohError, setSohError] = useState<string | null>(null);
  const sohInputRef = useRef<HTMLInputElement>(null);

  // Saved runs (persisted server-side)
  const [savedRuns, setSavedRuns] = useState<SavedRunSummary[]>([]);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [loadingSavedId, setLoadingSavedId] = useState<string | null>(null);

  // ── Load stages + active config ─────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      apiClient.get<StagesResponse>('/api/detection/stages'),
      apiClient.get<ActiveConfigResponse>('/api/detection/config'),
      apiClient.get<PresetResponse[]>('/api/detection/presets'),
      apiClient.get<SavedRunSummary[]>('/api/detection/runs').catch(() => [] as SavedRunSummary[]),
    ])
      .then(([s, c, ps, srs]) => {
        setStages(s);
        setConfig(c.config);
        setSavedJson(JSON.stringify(c.config));
        setPresets(ps);
        setSavedRuns(srs);
      })
      .catch(e => setLoadError(e instanceof ApiError ? e.message : 'Failed to load Detection Lab.'));
  }, []);

  const dirty = config != null && JSON.stringify(config) !== savedJson;

  const setToggle = (group: 'preprocessors' | 'decoders', name: string, on: boolean) =>
    setConfig(c => (c ? { ...c, [group]: { ...c[group], [name]: on } } : c));

  // ── Save active config ──────────────────────────────────────────────────────
  const saveConfig = async () => {
    if (!config) return;
    setSaving(true); setSaveMsg(null);
    try {
      const res = await apiClient.put<ActiveConfigResponse>('/api/detection/config', { config });
      setConfig(res.config);
      setSavedJson(JSON.stringify(res.config));
      setSaveMsg('Saved — active immediately.');
    } catch (e) {
      setSaveMsg(e instanceof ApiError ? `Save failed: ${e.message}` : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Run on test data (chunked, sequential) ──────────────────────────────────
  const runTest = async () => {
    if (!config || files.length === 0 || running) return;
    setRunning(true); setRunError(null);
    setProgress({ done: 0, total: files.length });

    const cfgSnapshot: PipelineConfig = JSON.parse(JSON.stringify(config));
    const allResults: FileResultOut[] = [];
    try {
      for (let i = 0; i < files.length; i += CHUNK_SIZE) {
        const chunk = files.slice(i, i + CHUNK_SIZE);
        const form = new FormData();
        chunk.forEach(f => form.append('files', f));
        form.append('config', JSON.stringify(cfgSnapshot));
        const res = await apiClient.postForm<RunResponse>('/api/detection/run', form);
        allResults.push(...res.files);
        setProgress({ done: Math.min(i + CHUNK_SIZE, files.length), total: files.length });
      }

      const ok = allResults.filter(r => r.success);
      const times = allResults.filter(r => !r.error).map(r => r.total_ms);
      const run: SessionRun = {
        id: ++runCounter.current,
        label: `Run #${runCounter.current}`,
        config: cfgSnapshot,
        files: allResults,
        decodeRate: allResults.length ? ok.length / allResults.length : 0,
        avgMs: times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0,
        p50: percentile(times, 50),
        p95: percentile(times, 95),
        ambiguousCount: allResults.filter(r => r.selection_ambiguous).length,
      };
      setRuns(rs => [...rs, run]);
      setActiveRunId(run.id);
    } catch (e) {
      setRunError(e instanceof ApiError ? e.message : 'Run failed — check connection.');
    } finally {
      setRunning(false);
    }
  };

  // ── Preset handlers ─────────────────────────────────────────────────────────
  const savePreset = async () => {
    if (!config || !savePresetName.trim()) return;
    setSavingPreset(true); setPresetMsg(null);
    try {
      const p = await apiClient.post<PresetResponse>('/api/detection/presets', {
        name: savePresetName.trim(), config,
      });
      setPresets(ps => [...ps, p].sort((a, b) => a.name.localeCompare(b.name)));
      setShowSavePreset(false); setSavePresetName('');
      setPresetMsg(`Preset "${p.name}" saved.`);
    } catch (e) {
      setPresetMsg(e instanceof ApiError ? `Save failed: ${e.message}` : 'Save failed.');
    } finally {
      setSavingPreset(false);
    }
  };

  const loadPreset = (p: PresetResponse) => {
    setConfig(p.config);
    setSavedJson(JSON.stringify(p.config));
    setPresetMsg(`Loaded "${p.name}".`);
  };

  const activatePreset = async (id: string) => {
    try {
      const updated = await apiClient.put<PresetResponse>(
        `/api/detection/presets/${id}/activate`, {},
      );
      setPresets(ps => ps.map(p => ({ ...p, is_active: p.id === updated.id })));
      setPresetMsg(`"${updated.name}" is now the active production config.`);
    } catch (e) {
      setPresetMsg(e instanceof ApiError ? `Activate failed: ${e.message}` : 'Activate failed.');
    }
  };

  const deletePreset = async (id: string) => {
    const name = presets.find(p => p.id === id)?.name ?? 'this preset';
    if (!confirm(`Delete preset "${name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/detection/presets/${id}`);
      setPresets(ps => ps.filter(p => p.id !== id));
      setPresetMsg(`Preset "${name}" deleted.`);
    } catch (e) {
      setPresetMsg(e instanceof ApiError ? `Delete failed: ${e.message}` : 'Delete failed.');
    }
  };

  // ── Batch run (all non-empty decoder subsets) ────────────────────────────────
  const runBatch = async () => {
    if (!config || files.length === 0 || running || batchRunning) return;
    const combos = decoderCombos(config);
    if (combos.length === 0) { setRunError('No decoders enabled — enable at least one.'); return; }
    setBatchRunning(true); setRunError(null);
    setBatchProgress({ combo: 0, totalCombos: combos.length, image: 0, totalImages: files.length });

    for (let ci = 0; ci < combos.length; ci++) {
      const { label, config: comboCfg } = combos[ci];
      setBatchProgress(p => ({ ...p, combo: ci + 1, image: 0 }));
      const allResults: FileResultOut[] = [];
      try {
        for (let i = 0; i < files.length; i += CHUNK_SIZE) {
          const chunk = files.slice(i, i + CHUNK_SIZE);
          const form = new FormData();
          chunk.forEach(f => form.append('files', f));
          form.append('config', JSON.stringify(comboCfg));
          const res = await apiClient.postForm<RunResponse>('/api/detection/run', form);
          allResults.push(...res.files);
          setBatchProgress(p => ({ ...p, image: Math.min(i + CHUNK_SIZE, files.length) }));
        }
        const ok = allResults.filter(r => r.success);
        const times = allResults.filter(r => !r.error).map(r => r.total_ms);
        const run: SessionRun = {
          id: ++runCounter.current,
          label: `Batch: ${label}`,
          config: comboCfg,
          files: allResults,
          decodeRate: allResults.length ? ok.length / allResults.length : 0,
          avgMs: times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0,
          p50: percentile(times, 50),
          p95: percentile(times, 95),
          ambiguousCount: allResults.filter(r => r.selection_ambiguous).length,
        };
        setRuns(rs => [...rs, run]);
        setActiveRunId(run.id);
      } catch {
        // skip failed combo, continue with rest
      }
    }
    setBatchRunning(false);
  };

  // ── Saved runs (persisted server-side) ──────────────────────────────────────
  const saveRunToServer = async (r: SessionRun) => {
    const label = window.prompt('Name this run (saved server-side with thumbnails):', r.label);
    if (!label || !label.trim()) return;
    setSavedMsg(null);
    try {
      const summary = await apiClient.post<SavedRunSummary>('/api/detection/runs', {
        label: label.trim(),
        config: r.config,
        files: r.files,
      });
      setSavedRuns(srs => [summary, ...srs]);
      setSavedMsg(`Run "${summary.label}" saved.`);
    } catch (e) {
      setSavedMsg(e instanceof ApiError ? `Save failed: ${e.message}` : 'Save failed.');
    }
  };

  const loadSavedRun = async (id: string) => {
    setLoadingSavedId(id); setSavedMsg(null);
    try {
      const d = await apiClient.get<SavedRunDetail>(`/api/detection/runs/${id}`);
      const run: SessionRun = {
        id: ++runCounter.current,
        label: `📌 ${d.label}`,
        config: d.config,
        files: d.files.map(f => ({
          success: false,
          selected_value: null,
          selected_decoder: null,
          detections: [],
          ocr: [],
          timings: [],
          total_ms: 0,
          image_width: 0,
          image_height: 0,
          preview_b64: null,
          ...f,
        }) as FileResultOut),
        decodeRate: d.decode_rate,
        avgMs: d.avg_ms,
        p50: d.p50_ms,
        p95: d.p95_ms,
        ambiguousCount: d.ambiguous_count,
      };
      setRuns(rs => [...rs, run]);
      setActiveRunId(run.id);
      setSavedMsg(`Loaded "${d.label}" into the comparison table.`);
    } catch (e) {
      setSavedMsg(e instanceof ApiError ? `Load failed: ${e.message}` : 'Load failed.');
    } finally {
      setLoadingSavedId(null);
    }
  };

  const deleteSavedRun = async (id: string) => {
    const label = savedRuns.find(r => r.id === id)?.label ?? 'this run';
    if (!confirm(`Delete saved run "${label}" and its thumbnails? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/detection/runs/${id}`);
      setSavedRuns(srs => srs.filter(r => r.id !== id));
      setSavedMsg(`Deleted "${label}".`);
    } catch (e) {
      setSavedMsg(e instanceof ApiError ? `Delete failed: ${e.message}` : 'Delete failed.');
    }
  };

  // ── SOH ground truth (optional) ─────────────────────────────────────────────
  const uploadSoh = async (f: File) => {
    setSohLoading(true); setSohError(null);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await apiClient.postForm<SOHGroundTruth>('/api/detection/soh', form);
      setSoh(res);
    } catch (e) {
      setSohError(e instanceof ApiError ? e.message : 'Failed to load SOH file.');
    } finally {
      setSohLoading(false);
      if (sohInputRef.current) sohInputRef.current.value = '';
    }
  };

  const sohSets: SOHSets | null = useMemo(
    () => soh && {
      locations: new Set(soh.locations),
      sscc: new Set(soh.sscc),
      palletCodes: new Set(soh.pallet_codes),
      lots: new Set(soh.lots),
    },
    [soh],
  );

  // SOH-verified rate per run — computed at render time from the current SOH
  // sets, so loading a file retroactively validates runs already in the table.
  const sohRates = useMemo(() => {
    if (!sohSets) return null;
    const m = new Map<number, number>();
    for (const r of runs) {
      const verified = r.files.filter(f => {
        const s = fileSohStatus(f, sohSets);
        return s === 'qr' || s === 'ocr';
      }).length;
      m.set(r.id, r.files.length ? verified / r.files.length : 0);
    }
    return m;
  }, [runs, sohSets]);

  const activeRun = useMemo(
    () => runs.find(r => r.id === activeRunId) ?? null,
    [runs, activeRunId],
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loadError) return <div className="text-sm text-red-600 py-4">⚠ {loadError}</div>;
  if (!stages || !config) return <div className="text-sm text-muted-foreground py-4">Loading Detection Lab…</div>;

  return (
    <div className="space-y-6 max-w-5xl">

      {/* ── ① Toggle panel ─────────────────────────────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Pipeline configuration</h3>
          <div className="flex items-center gap-2">
            {saveMsg && <span className="text-xs text-muted-foreground">{saveMsg}</span>}
            <button
              className="btn btn--primary btn--sm"
              disabled={!dirty || saving}
              onClick={saveConfig}
              title="Persist as the active config — applies at runtime, no redeploy"
            >
              {saving ? 'Saving…' : dirty ? 'Save as active config' : 'Saved'}
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preprocessing</p>
            {stages.preprocessors.map(s => (
              <StageToggle key={s.name} stage={s}
                checked={!!config.preprocessors[s.name]}
                onChange={on => setToggle('preprocessors', s.name, on)} />
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Decoders</p>
            {stages.decoders.map(s => (
              <StageToggle key={s.name} stage={s}
                checked={!!config.decoders[s.name]}
                onChange={on => setToggle('decoders', s.name, on)} />
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">OCR</p>
            {stages.ocr.map(s => (
              <StageToggle key={s.name} stage={s}
                checked={config.ocr.tesseract}
                onChange={on => setConfig(c => (c ? { ...c, ocr: { ...c.ocr, tesseract: on } } : c))} />
            ))}
            <select
              className="form-input text-sm w-full"
              value={config.ocr.mode}
              disabled={!config.ocr.tesseract}
              onChange={e => setConfig(c => (c ? { ...c, ocr: { ...c.ocr, mode: e.target.value as 'fallback' | 'parallel' } } : c))}
            >
              <option value="fallback">Fallback — only when decoders find nothing</option>
              <option value="parallel">Parallel — always run OCR</option>
            </select>
          </div>
        </div>
      </section>

      {/* ── ② Presets ──────────────────────────────────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Presets</h3>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => { setShowSavePreset(v => !v); setSavePresetName(''); setPresetMsg(null); }}
          >
            + Save current as preset
          </button>
        </div>

        {showSavePreset && (
          <div className="flex items-center gap-2">
            <input
              className="form-input text-sm flex-1"
              placeholder="Preset name…"
              value={savePresetName}
              onChange={e => setSavePresetName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') savePreset(); if (e.key === 'Escape') setShowSavePreset(false); }}
              autoFocus
            />
            <button className="btn btn--primary btn--sm" disabled={!savePresetName.trim() || savingPreset} onClick={savePreset}>
              {savingPreset ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => { setShowSavePreset(false); setSavePresetName(''); }}>
              Cancel
            </button>
          </div>
        )}

        {presetMsg && <p className="text-xs text-muted-foreground">{presetMsg}</p>}

        {presets.length === 0 ? (
          <p className="text-xs text-muted-foreground">No presets saved yet. Create one above.</p>
        ) : (
          <div className="space-y-1.5">
            {presets.map(p => (
              <div
                key={p.id}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${p.is_active ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
              >
                <span className="font-medium flex-1 truncate" title={p.name}>{p.name}</span>
                {p.is_active && (
                  <span className="text-[10px] font-semibold text-blue-700 px-1.5 py-0.5 rounded-full bg-blue-100 shrink-0">
                    ACTIVE
                  </span>
                )}
                <span className="text-xs text-muted-foreground font-mono truncate max-w-[180px] hidden sm:inline">
                  {configSummary(p.config)}
                </span>
                <button className="btn btn--ghost btn--xs" onClick={() => loadPreset(p)} title="Load this config into the toggles">
                  Load
                </button>
                {!p.is_active && (
                  <button className="btn btn--ghost btn--xs" onClick={() => activatePreset(p.id)} title="Set as production active config">
                    Set active
                  </button>
                )}
                {!p.is_active && (
                  <button className="btn btn--ghost btn--xs text-red-600" onClick={() => deletePreset(p.id)} title="Delete preset">
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── ③ Test data + run ──────────────────────────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-3">
        <h3 className="font-semibold text-sm">Run on test data</h3>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            className="text-sm"
            onChange={e => {
              const picked = Array.from(e.target.files ?? []).slice(0, MAX_TOTAL_FILES);
              setFiles(picked);
            }}
          />
          <button
            className="btn btn--primary btn--sm"
            disabled={running || batchRunning || files.length === 0}
            onClick={runTest}
          >
            {running
              ? `Running… ${progress.done}/${progress.total}`
              : `▶ Run on ${files.length || 'test'} image${files.length === 1 ? '' : 's'}`}
          </button>
          <button
            className="btn btn--secondary btn--sm"
            disabled={running || batchRunning || files.length === 0}
            onClick={runBatch}
            title="Run every non-empty subset of enabled decoders against the selected images and compare results"
          >
            {batchRunning
              ? `Batch… combo ${batchProgress.combo}/${batchProgress.totalCombos} · img ${batchProgress.image}/${batchProgress.totalImages}`
              : '⊕ Run ALL combinations'}
          </button>
          {files.length > 0 && !running && !batchRunning && (
            <button className="btn btn--ghost btn--sm" onClick={() => {
              setFiles([]);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}>
              Clear selection
            </button>
          )}
          <span className="text-xs text-muted-foreground">
            Uses the toggles above (saved or not) · max {MAX_TOTAL_FILES} images · processed in batches of {CHUNK_SIZE}
          </span>
        </div>
        {runError && <p className="text-xs text-red-600">⚠ {runError}</p>}

        {/* SOH ground truth — optional */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            SOH ground truth <span className="normal-case font-normal">(optional)</span>
          </span>
          {soh ? (
            <>
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                ✓ {soh.filename} · {soh.rows.toLocaleString()} rows
                {[
                  soh.locations.length && `${soh.locations.length.toLocaleString()} locations`,
                  soh.sscc.length && `${soh.sscc.length.toLocaleString()} SSCC`,
                  soh.pallet_codes.length && `${soh.pallet_codes.length.toLocaleString()} pallet codes`,
                  soh.lots.length && `${soh.lots.length.toLocaleString()} lots`,
                ].filter(Boolean).map(s => ` · ${s}`).join('')}
              </span>
              <button className="btn btn--ghost btn--xs" onClick={() => setSoh(null)}>
                ✕ Clear
              </button>
            </>
          ) : (
            <input
              ref={sohInputRef}
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv"
              className="text-sm"
              disabled={sohLoading}
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadSoh(f); }}
            />
          )}
          {sohLoading && <span className="text-xs text-muted-foreground">Parsing…</span>}
          {sohError && <span className="text-xs text-red-600">⚠ {sohError}</span>}
          <span className="text-xs text-muted-foreground">
            Decoded values are checked against Location / SSCC / Lot — a match upgrades a detection to SOH-verified. Without it, a positive decode still counts as basic validation.
          </span>
        </div>
      </section>

      {/* ── ④ Comparison table (session-only) ──────────────────────────────── */}
      {runs.length > 0 && (
        <section className="rounded-xl border bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Run comparison <span className="text-xs text-muted-foreground font-normal">(this session only)</span></h3>
            <div className="flex gap-1">
              <button className="btn btn--ghost btn--xs" onClick={() => { setSortKey('decodeRate'); setSortDesc(true); }}>
                {sortKey === 'decodeRate' ? '▼ Decode rate' : 'Decode rate'}
              </button>
              <button className="btn btn--ghost btn--xs" onClick={() => { setSortKey('avgMs'); setSortDesc(false); }}>
                {sortKey === 'avgMs' ? '▲ Avg ms' : 'Avg ms'}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => { setRuns([]); setActiveRunId(null); }}>
                Clear runs
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {(() => {
                const showAmbiguous = runs.some(r => r.ambiguousCount > 0);
                return (
                  <>
                    <thead>
                      <tr className="text-xs text-muted-foreground uppercase tracking-wide text-left border-b">
                        <th className="py-1.5 pr-3">Run</th>
                        <th className="py-1.5 pr-3">Configuration</th>
                        <th className="py-1.5 pr-3 text-right">Files</th>
                        <th className="py-1.5 pr-3 text-right">Decode rate</th>
                        {sohRates && <th className="py-1.5 pr-3 text-right">SOH ✓</th>}
                        {showAmbiguous && (
                          <th className="py-1.5 pr-3 text-right" title="Number of files where the QR target isolator found two equally-prominent codes and fell back to the S-rule">
                            Ambiguous
                          </th>
                        )}
                        <th className="py-1.5 pr-3 text-right">Avg ms</th>
                        <th className="py-1.5 pr-3 text-right">p50</th>
                        <th className="py-1.5 pr-3 text-right">p95</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRuns(runs, sortKey, sortDesc).map(r => (
                        <tr
                          key={r.id}
                          className={`border-b last:border-0 cursor-pointer hover:bg-gray-50 ${r.id === activeRunId ? 'bg-blue-50' : ''}`}
                          onClick={() => setActiveRunId(r.id)}
                        >
                          <td className="py-1.5 pr-3 font-medium">{r.label}</td>
                          <td className="py-1.5 pr-3 font-mono text-xs">{configSummary(r.config)}</td>
                          <td className="py-1.5 pr-3 text-right">{r.files.length}</td>
                          <td className="py-1.5 pr-3 text-right font-semibold">
                            <span className={r.decodeRate >= 0.9 ? 'text-green-700' : r.decodeRate >= 0.7 ? 'text-amber-600' : 'text-red-600'}>
                              {(r.decodeRate * 100).toFixed(0)}%
                            </span>
                          </td>
                          {sohRates && (
                            <td className="py-1.5 pr-3 text-right font-semibold">
                              {(() => {
                                const rate = sohRates.get(r.id) ?? 0;
                                return (
                                  <span className={rate >= 0.9 ? 'text-emerald-700' : rate >= 0.7 ? 'text-amber-600' : 'text-red-600'}>
                                    {(rate * 100).toFixed(0)}%
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {showAmbiguous && (
                            <td className="py-1.5 pr-3 text-right">
                              {r.ambiguousCount > 0
                                ? <span className="font-semibold text-amber-700">{r.ambiguousCount}</span>
                                : <span className="text-muted-foreground">—</span>}
                            </td>
                          )}
                          <td className="py-1.5 pr-3 text-right">{r.avgMs.toFixed(0)}</td>
                          <td className="py-1.5 pr-3 text-right">{r.p50.toFixed(0)}</td>
                          <td className="py-1.5 pr-3 text-right">{r.p95.toFixed(0)}</td>
                          <td className="py-1.5 text-right">
                            {!r.label.startsWith('📌') && (
                              <button
                                className="btn btn--ghost btn--xs"
                                title="Save this run server-side (results, filenames, thumbnails)"
                                onClick={e => { e.stopPropagation(); saveRunToServer(r); }}
                              >
                                💾
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                );
              })()}
            </table>
          </div>
        </section>
      )}

      {/* ── ④b Saved runs (persisted server-side) ──────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">
            Saved runs <span className="text-xs text-muted-foreground font-normal">(persisted — survive page reloads)</span>
          </h3>
          {savedMsg && <span className="text-xs text-muted-foreground">{savedMsg}</span>}
        </div>
        {savedRuns.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No saved runs yet. Run a test, then click 💾 next to a run in the comparison table.
          </p>
        ) : (
          <div className="space-y-1.5">
            {savedRuns.map(sr => (
              <div key={sr.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-white">
                <span className="font-medium truncate" title={sr.label}>{sr.label}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {new Date(sr.created_at).toLocaleString('en-GB', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                <span className="text-xs text-muted-foreground font-mono truncate max-w-[180px] hidden sm:inline">
                  {configSummary(sr.config)}
                </span>
                <span className="text-xs text-muted-foreground ml-auto shrink-0">
                  {sr.file_count} files ·{' '}
                  <span className={sr.decode_rate >= 0.9 ? 'text-green-700 font-semibold' : sr.decode_rate >= 0.7 ? 'text-amber-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {(sr.decode_rate * 100).toFixed(0)}%
                  </span>
                  {sr.ambiguous_count > 0 && <span className="text-amber-700"> · ⚠{sr.ambiguous_count}</span>}
                </span>
                <button
                  className="btn btn--ghost btn--xs"
                  disabled={loadingSavedId === sr.id}
                  onClick={() => loadSavedRun(sr.id)}
                  title="Load into the comparison table"
                >
                  {loadingSavedId === sr.id ? 'Loading…' : 'Load'}
                </button>
                <button
                  className="btn btn--ghost btn--xs text-red-600"
                  onClick={() => deleteSavedRun(sr.id)}
                  title="Delete this saved run and its thumbnails"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── ⑤ Per-file results for the selected run ─────────────────────────── */}
      {activeRun && (
        <section className="rounded-xl border bg-white p-4 space-y-3">
          <h3 className="font-semibold text-sm">
            {activeRun.label} — per-file results
            <span className="ml-2 text-xs font-normal text-muted-foreground font-mono">{configSummary(activeRun.config)}</span>
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeRun.files.map(f => (
              <div key={f.filename + f.total_ms} className="rounded-lg border overflow-hidden">
                <div
                  className="bg-gray-100 aspect-video flex items-center justify-center cursor-zoom-in overflow-hidden"
                  onClick={() => f.preview_b64 && setZoomFile(f)}
                  title="Click to enlarge"
                >
                  {f.preview_b64
                    ? <img src={f.preview_b64} alt={f.filename} className="w-full h-full object-cover" />
                    : <span className="text-xs text-muted-foreground">no preview</span>}
                </div>
                <div className="p-2.5 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${f.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                      {f.success ? '✓' : '✗'}
                    </span>
                    {f.selection_ambiguous && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 bg-amber-100 text-amber-800"
                        title={f.selection_audit_reason ?? 'Ambiguous QR target — two equally-prominent codes of the same type found'}
                      >
                        ⚠ Ambiguous
                      </span>
                    )}
                    {!f.selection_ambiguous && !f.selected_value && f.sku_value && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 bg-orange-100 text-orange-800"
                        title={f.selection_audit_reason ?? 'No decoded value matches a location format — tag missing or defaced'}
                      >
                        ⚠ No location QR
                      </span>
                    )}
                    {sohSets && (() => {
                      const s = fileSohStatus(f, sohSets);
                      if (!s) return null;
                      const b = SOH_BADGE[s];
                      return (
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${b.cls}`}
                          title={s === 'qr' ? 'Decoded value found in the SOH file'
                            : s === 'ocr' ? 'OCR text contains an SOH lot/SSCC/location'
                            : 'Decoded value not found in the SOH file — possible misread or outdated SOH'}
                        >
                          {b.label}
                        </span>
                      );
                    })()}
                    <span className="text-xs font-mono truncate" title={f.filename}>{f.filename}</span>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">{f.total_ms.toFixed(0)} ms</span>
                  </div>
                  {f.selected_value && (
                    <p className="text-xs">
                      <span className="text-[10px] font-semibold text-gray-500 uppercase mr-1">Loc</span>
                      <code className="font-semibold">{f.selected_value}</code>
                      {f.selected_decoder && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-white text-[10px] font-semibold"
                          style={{ background: DECODER_COLORS[f.selected_decoder] ?? '#6b7280' }}>
                          {decoderLabel(f.selected_decoder)}
                        </span>
                      )}
                    </p>
                  )}
                  {(() => {
                    // All distinct SKU labels, ranked; old saved runs only carry the single sku_value.
                    const skus = f.skus?.length
                      ? f.skus
                      : f.sku_value ? [{ value: f.sku_value, decoder: f.sku_decoder }] : [];
                    return skus.map((s, i) => {
                      const sohHit = sohSets ? matchSoh(s.value, sohSets) : null;
                      return (
                        <p key={s.value} className="text-xs">
                          <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1 py-0.5 rounded uppercase mr-1">
                            {skus.length > 1 ? `SKU${i + 1}` : 'SKU'}
                          </span>
                          <code className="font-semibold">{s.value}</code>
                          {s.decoder && (
                            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-white text-[10px] font-semibold"
                              style={{ background: DECODER_COLORS[s.decoder] ?? '#6b7280' }}>
                              {decoderLabel(s.decoder)}
                            </span>
                          )}
                          {sohHit && (
                            <span
                              className="ml-1.5 px-1 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800"
                              title={`This label identifies the inventory in the SOH file (${sohHit})`}
                            >
                              SOH ✓
                            </span>
                          )}
                        </p>
                      );
                    });
                  })()}
                  {f.error && <p className="text-xs text-red-600 truncate" title={f.error}>{f.error}</p>}
                  {!f.selected_value && !f.error && f.ocr.length > 0 && (
                    <p className="text-xs text-muted-foreground truncate" title={f.ocr.map(o => o.text).join(', ')}>
                      OCR: {f.ocr.slice(0, 3).map(o => o.text).join(', ')}
                    </p>
                  )}
                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer">Stage timings</summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {f.timings.map((t, i) => (
                        <li key={i}>
                          {t.available
                            ? `${t.name} ${t.ms.toFixed(0)}ms${t.kind !== 'preprocessor' ? ` (${t.detections})` : ''}`
                            : `${t.name} — unavailable`}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {zoomFile && <PreviewModal file={zoomFile} onClose={() => setZoomFile(null)} />}
    </div>
  );
}
